import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import MarqueeBar from './MarqueeBar';
import OfflineIndicator from '../UI/OfflineIndicator';
import ThemeToggle from '../UI/ThemeToggle';
import {
  HiOutlineHome,
  HiOutlineUsers,
  HiOutlineDocumentReport,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineLogout,
  HiOutlineLocationMarker,
} from 'react-icons/hi';
import logo from '../../assets/logo.png';

const navItems = [
  { path: '/app/', label: 'Dashboard', icon: HiOutlineHome, exact: true },
  { path: '/app/my-reports', label: 'My Reports', icon: HiOutlineDocumentReport, exact: false },
  { path: '/app/leads', label: 'My Leads', icon: HiOutlineUsers, exact: false },
  { path: '/app/visits', label: 'My Visits', icon: HiOutlineLocationMarker, exact: false },
];

const getInitials = (user) =>
  `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}` || 'U';

const UserSidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.path || location.pathname === item.path.replace(/\/$/, '')
      : location.pathname.startsWith(item.path);

  const handleNavClick = () => {
    if (window.innerWidth < 1024) onClose();
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-secondary-900/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 z-50 top-0 h-screen w-64
          bg-gradient-to-b from-white to-secondary-50/30 border-r border-secondary-200/50
          flex flex-col shadow-xl lg:shadow-none
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Logo */}
        <div className="relative flex items-center justify-center px-3 py-4 border-b border-secondary-100/50">
          <img
            src={logo}
            alt="TeleValley"
            className="brand-logo w-full h-auto max-h-14 object-contain"
          />
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-2 rounded-xl hover:bg-secondary-100 lg:hidden transition-colors"
            aria-label="Close sidebar"
          >
            <HiOutlineX className="w-5 h-5 text-secondary-400" />
          </button>
        </div>

        {/* User Info */}
        <div className="px-3 py-3">
          <div className="px-3 py-3 rounded-xl bg-gradient-to-r from-primary-50 to-indigo-50 border border-primary-100/50">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md ring-2 ring-white">
                <span className="text-white font-semibold text-sm">{getInitials(user)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-secondary-900 text-sm truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-secondary-500 truncate">{user?.email || user?.phone}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          <p className="px-3 text-xs font-semibold text-secondary-400 uppercase tracking-wider mb-2">
            Menu
          </p>
          <ul className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);

              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={`
                      flex items-center gap-3 px-3 py-2.5 rounded-xl
                      transition-all duration-200 relative
                      ${active
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/25'
                        : 'text-secondary-600 hover:bg-secondary-100 hover:text-secondary-900'
                      }
                    `}
                  >
                    {active && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-white/30 rounded-r-full" />
                    )}
                    <Icon className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white' : 'text-secondary-400'}`} />
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="p-3 border-t border-secondary-100/50">
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-secondary-600 hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 hover:text-red-600 transition-all duration-200"
          >
            <HiOutlineLogout className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export const UserMobileHeader = ({ onMenuClick }) => {
  const { user } = useAuth();

  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-secondary-200/50 shadow-sm">
      <div className="flex items-center justify-between px-4 py-2.5">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 rounded-xl text-secondary-600 hover:bg-secondary-100 transition-colors"
          aria-label="Open menu"
        >
          <HiOutlineMenu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2">
          <div className="brand-logo-box w-8 h-8 rounded-xl overflow-hidden shadow-md flex items-center justify-center">
            <img src={logo} alt="TeleValley" className="w-full h-full object-contain" />
          </div>
          <span className="font-bold text-secondary-900">TeleValley</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-50 flex items-center justify-center ring-2 ring-white shadow-sm">
            <span className="text-primary-600 font-semibold text-xs">{getInitials(user)}</span>
          </div>
        </div>
      </div>
    </header>
  );
};

const UserLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50/30">
      {/* Offline banner / back-online toast, same as the admin panel */}
      <OfflineIndicator />

      {/* Dark / light mode toggle — fixed bottom-right, same as the admin panel */}
      <div className="hidden lg:block fixed bottom-5 right-5 z-40">
        <ThemeToggle />
      </div>

      <UserMobileHeader onMenuClick={() => setSidebarOpen(true)} />

      <div className="flex">
        <UserSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        <main className="flex-1 min-h-screen min-w-0 lg:ml-64">
          <MarqueeBar />

          <div className="pt-16 lg:pt-0 w-full h-full overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default UserLayout;
